import React, { useEffect, useState } from "react";
import { AdminTable } from "../Table/AntTable";
import type { DataTableColumn, ActionConfig } from "../Table/AntTable";
import { message } from "antd";
import {
  CustomChallenge,
  getLoserOfTheWeek,
} from "../weeklyChallenges/customChallenge";
import { AdminDeleteModal } from "../AdminDeleteModal";
import { deleteLotw } from "../../services/firebase";
import EditLoserOfTheWeekModal from "../ui/EditLoserOfTheWeekModal";

const ViewLotw: React.FC = () => {
  const [data, setData] = useState<CustomChallenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingLotwId, setEditingLotwId] = useState<string | null>(null);
  const [deletingLotwId, setDeletingLotwId] = useState<string | null>(null);

  // Define columns with custom rendering
  const columns: DataTableColumn<CustomChallenge>[] = [
    {
      title: "Thumbnail",
      dataIndex: "image",
      sorter: true,
      width: 100,
      render: (value: string) => {
        if (!value) return null;
        return (
          <img
            src={value}
            alt="Thumbnail"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
    {
      title: "Name",
      dataIndex: "title",
      sorter: true,
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: "Author",
      dataIndex: "author",
      sorter: true,
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: "Year",
      dataIndex: "year",
      align: "center",
      sorter: (a, b) => Number(a.year) - Number(b.year),
    },
    {
      title: "Week",
      dataIndex: "week",
      align: "center",
      sorter: (a, b) => Number(a.week) - Number(b.week),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 120,
      sorter: (a, b) => {
        const getTime = (value: any) => {
          if (!value) return 0;
          if (value.toDate) return value.toDate().getTime();
          if (value.seconds) return value.seconds * 1000;
          if (value instanceof Date) return value.getTime();
          return 0;
        };
        return getTime(a.createdAt) - getTime(b.createdAt);
      },
      render: (value: any) => {
        if (!value) return "-";
        try {
          let date: Date;
          // Handle Firebase Timestamp
          if (value.toDate) {
            date = value.toDate();
          } else if (value.seconds) {
            date = new Date(value.seconds * 1000);
          } else if (value instanceof Date) {
            date = value;
          } else {
            date = new Date(value);
          }
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch (error) {
          return "-";
        }
      },
    },
  ];

  const fetchLotw = async () => {
    setLoading(true);
    try {
      const fetched = await getLoserOfTheWeek();
      setData(fetched);
      //setAllChallenges(fetched.sort((a, b) => b.year.localeCompare(a.year)));
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch loser of the week");
    } finally {
      setLoading(false);
      console.log("Fetched loser of the week");
    }
  };

  useEffect(() => {
    fetchLotw();
  }, []);

  // Define action handlers
  const handleEdit = (record: CustomChallenge) => {
    message.info(`Editing loser of the week: ${record.title}`);
    if (record.id) {
      setEditingLotwId(record.id);
    }
  };

  const handleDelete = (record: CustomChallenge) => {
    if (record.id) {
      setDeletingLotwId(record.id);
    }
  };

  const onDeleteLotw = async (lotwId: string) => {
    try {
      await deleteLotw(lotwId);
      message.success("Loser of the week deleted successfully");
      setDeletingLotwId(null);
      fetchLotw();
    } catch (error) {
      console.error("Failed to delete loser of the week:", error);
    }
  };

  const actions: ActionConfig<CustomChallenge> = {
    onEdit: handleEdit,
    onDelete: handleDelete,
    editText: "Edit",
    deleteText: "Delete",
  };

  return (
    <div style={{ padding: "24px" }}>
      <AdminTable<CustomChallenge>
        columns={columns}
        data={data}
        rowKey="id"
        loading={loading}
        actions={actions}
        pageSize={10}
        searchable={true}
        searchPlaceholder="Search users by any field..."
        bordered={true}
        size="middle"
        showSizeChanger={true}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      {editingLotwId && (
        <EditLoserOfTheWeekModal
          loserId={editingLotwId}
          isAdmin={true}
          onSaveSuccess={() => {
            message.success("Loser of the week updated successfully");
            setEditingLotwId(null);
            fetchLotw();
          }}
          isEdit={true}
          onClose={() => setEditingLotwId(null)}
        />
      )}
      {deletingLotwId && (
        <AdminDeleteModal
          onConfirm={() => onDeleteLotw(deletingLotwId)}
          description="Are you sure you want to delete this loser of the week? This action cannot be undone."
          isDeleting={true}
          onClose={() => setDeletingLotwId(null)}
        />
      )}
    </div>
  );
};

export default ViewLotw;
